import { useState, useEffect } from 'react'
import { Bell, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { previewSound, notificationsEnabled } from '@/lib/sounds'
import { usePage } from '@inertiajs/react'

const DISMISSED_KEY = 'banshee_notification_banner_dismissed'

interface SharedProps {
  current_account_user?: { sound_preference: 'chirp' | 'key' | 'echo' | 'none' }
}

export function NotificationPermissionBanner() {
  const [show, setShow] = useState(false)
  const pageProps = usePage().props as unknown as SharedProps
  const soundPreference = pageProps.current_account_user?.sound_preference || 'chirp'

  useEffect(() => {
    // Don't show if notifications already granted or user dismissed
    if (notificationsEnabled()) return
    if (typeof window !== 'undefined' && localStorage.getItem(DISMISSED_KEY)) return

    // Show banner after a short delay
    const timer = setTimeout(() => setShow(true), 1000)
    return () => clearTimeout(timer)
  }, [])

  const handleEnable = async () => {
    // Play sound immediately - this click unlocks audio!
    if (soundPreference !== 'none') {
      previewSound(soundPreference)
    }

    // Also request notification permission for desktop notifications
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission()
    }

    setShow(false)
    localStorage.setItem(DISMISSED_KEY, 'true')
  }

  const handleDismiss = () => {
    setShow(false)
    localStorage.setItem(DISMISSED_KEY, 'true')
  }

  if (!show) return null

  return (
    <div className="bg-primary/10 border-b border-primary/20 px-4 py-2 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <Bell className="size-4 text-primary" />
        <span className="text-sm">
          Enable notifications to hear sounds when new messages arrive
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={handleEnable}
        >
          Enable Notifications
        </Button>
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-muted rounded cursor-pointer"
          aria-label="Dismiss"
        >
          <X className="size-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  )
}
